/**
 * Standards/minimum-rate guardrails — the FastCMS analog of V1's preloaded
 * draystandards / deliverystandards JS hashes and checkfield_* warnings
 * (quote-dev editrecord_quote.pl:3614-3720, defs_quote.pl onChange hooks).
 *
 * Pure lookup helpers over the GET /quotes/rate-standards payload. UI calls
 * these to render a "standard $X / min $Y" hint and a red below-minimum
 * warning next to rate inputs. (V1 enforces the floor via the
 * bypassminimumrate ACL; FastCMS warns and lets the save through — server
 * enforcement can be layered on once a bypass permission exists.)
 */
import type { RateStandards } from '../../services/quote.service';

export interface RateGuard {
  standardRate: number;
  minimumRate: number;
}

const num = (v: string | undefined): number => Number(v) || 0;

/** draystandards keyed by warehouse + ratezone (V1 getDrayStandards). */
export function drayGuard(std: RateStandards | null, warehouse: string, rateZone: string): RateGuard | null {
  if (!std || !warehouse || !rateZone) return null;
  const row = std.dray.find((r) => r.warehouse === warehouse && r.rateZone === rateZone);
  return row ? { standardRate: num(row.standardRate), minimumRate: num(row.minimumRate) } : null;
}

/** deliverystandards keyed by ratezone + zip, falling back to city+state
 *  (V1 getDeliveryStandards: zip for LOCAL, city/state for OTR). */
export function deliveryGuard(
  std: RateStandards | null,
  rateZone: string,
  zip: string,
  city: string,
  state: string,
): RateGuard | null {
  if (!std || !rateZone) return null;
  const byZip = zip ? std.delivery.find((r) => r.rateZone === rateZone && r.zip === zip) : undefined;
  const row = byZip
    ?? (city && state
      ? std.delivery.find((r) => r.rateZone === rateZone && r.city === city.toUpperCase() && r.state === state.toUpperCase())
      : undefined);
  return row ? { standardRate: num(row.standardRate), minimumRate: num(row.minimumRate) } : null;
}

/** transloadstandards keyed by whsetype + packagetype + movetype, with the
 *  rate pair picked by container size (20 vs 40-class). */
export function transloadGuard(
  std: RateStandards | null,
  whseType: string,
  packageType: string,
  moveType: string,
  size: string,
): RateGuard | null {
  if (!std || !whseType || !packageType || !moveType) return null;
  const row = std.transload.find(
    (r) => r.whseType === whseType && r.packageType === packageType && r.moveType === moveType,
  );
  if (!row) return null;
  const is20 = size === '20';
  return {
    standardRate: num(is20 ? row.size20StandardRate : row.size40StandardRate),
    minimumRate: num(is20 ? row.size20MinimumRate : row.size40MinimumRate),
  };
}

/** True when a non-zero entered rate is under the guard's minimum. */
export function belowMinimum(guard: RateGuard | null, rate: string | number | undefined): boolean {
  if (!guard || guard.minimumRate <= 0) return false;
  const r = Number(rate) || 0;
  return r > 0 && r < guard.minimumRate;
}

/** Suggested mileage rate for a lane (V1 ratezonemileage / ratezone_mileagerate,
 *  editrecord_quote.pl:3659-3672). Tiers are keyed by ratezone + a miles
 *  threshold; we pick the highest tier whose threshold is <= the lane's miles,
 *  falling back to the lowest tier for a sub-threshold lane. Advisory only.
 *  (V1's exact band-selection JS isn't in the repo — documented best-effort.) */
export function mileageRateFor(
  mileage: RateStandards['mileage'] | undefined,
  rateZone: string,
  miles: string | number | undefined,
): { rateType: string; mileageRate: number } | null {
  const m = Number(miles) || 0;
  if (!mileage || !rateZone || m <= 0) return null;
  const tiers = mileage.filter((t) => t.rateZone === rateZone).sort((a, b) => a.miles - b.miles);
  if (!tiers.length) return null;
  let chosen = tiers[0];
  for (const t of tiers) {
    if (t.miles <= m) chosen = t; else break;
  }
  return { rateType: chosen.rateType, mileageRate: Number(chosen.mileageRate) || 0 };
}

/** Should the below-minimum Save-gate block? (V1 bypassminimumrate ACL,
 *  editrecord_quote.pl:796-804.) FastCMS has no feature-permission slot, so the
 *  bypass keys on an Azure AD group. The gate is DORMANT until that group id is
 *  configured (`bypassGroupId` empty) — it blocks no one — and once set, blocks
 *  a below-min save only for users who are NOT in the group. */
export function minRateBlocksSave(
  anyBelowMin: boolean,
  bypassGroupId: string,
  inBypassGroup: boolean,
): boolean {
  if (!anyBelowMin) return false;
  if (!bypassGroupId) return false; // not yet configured -> advisory only
  return !inBypassGroup;
}
